import { IlbaResource } from "@/types/Ilba"

export type IlbaProductRef = { code: string; name: string }

/**
 * Preference-module ILBA notice: criteria flagged for transfer and no
 * preference expiry date on file (see brief — distinct from homepage banner rules).
 */
export function isPreferenceModuleTransferNoticeEligible(
  ilba: IlbaResource
): boolean {
  return ilba.criteria?.code === "Y" && !ilba.expiryDate
}

/**
 * Whether the preference module should render at all.
 */
export function shouldShowIlbaPreferenceModule({
  hasGsu,
  hasWss,
}: {
  hasGsu: boolean
  hasWss: boolean
}): boolean {
  return hasGsu || hasWss
}

/**
 * Returns ILBA products the member owns (GSU and/or WSS), GSU first.
 */
export function getOwnedIlbaProducts({
  hasGsu,
  hasWss,
  products,
}: {
  hasGsu: boolean
  hasWss: boolean
  products: readonly IlbaProductRef[]
}): IlbaProductRef[] {
  return products.filter(
    (product) =>
      (product.code === "GSU" && hasGsu) || (product.code === "WSS" && hasWss)
  )
}

type PreferenceFormProductSource = IlbaProductRef & {
  expiryDate?: string
  transferDate?: string
}

/**
 * Products that should render a preference form — owned accounts,
 * accounts with an expiring preference (renewal), and transfer-notice accounts.
 * GSU is always listed before WSS.
 */
export function getPreferenceFormProducts({
  ownedProducts,
  expiringProducts,
  transferNoticeProducts,
}: {
  ownedProducts: readonly IlbaProductRef[]
  expiringProducts: readonly PreferenceFormProductSource[]
  transferNoticeProducts: readonly PreferenceFormProductSource[]
}): IlbaProductRef[] {
  const productMap = new Map<string, IlbaProductRef>()

  for (const product of [
    ...ownedProducts,
    ...expiringProducts,
    ...transferNoticeProducts,
  ]) {
    productMap.set(product.code, { code: product.code, name: product.name })
  }

  return (["GSU", "WSS"] as const)
    .filter((code) => productMap.has(code))
    .map((code) => productMap.get(code)!)
}

/** Both GSU and WSS are flagged for ATO transfer in homepage banner data. */
export function areBothIlbaProductsAffected(
  affectedProducts: readonly IlbaProductRef[]
): boolean {
  const affectedCodes = new Set(affectedProducts.map((product) => product.code))
  return affectedCodes.has("GSU") && affectedCodes.has("WSS")
}

/** Campaign flags; isDuringBlackout = election UI hidden (ATO cut-off), not Call us. */
export type IlbaCampaignState = {
  isWithinCampaign: boolean
  isBeforeBlackout: boolean
  isDuringBlackout: boolean
  // last inclusive day members can submit an ILBA election — shown in CMS copy ({action_date} / {blackout_date})
  actionDate: string
  blackoutStartDate: string
  blackoutEndDate: string
}

type IlbaCampaignCsvRow = Record<string, string>

type IlbaCampaignRow = {
  campaignStart: Date
  campaignEnd: Date
  actionDate: Date
  blackoutStart: Date
  blackoutEnd: Date
}

const EMPTY_ILBA_CAMPAIGN_STATE: IlbaCampaignState = {
  isWithinCampaign: false,
  isBeforeBlackout: false,
  isDuringBlackout: false,
  actionDate: "",
  blackoutStartDate: "",
  blackoutEndDate: "",
}

/** Parses a CMS date string (DD/MM/YYYY) into a local midnight Date. */
function parseCmsDate(dateStr: string | undefined): Date | null {
  if (!dateStr) {
    return null
  }

  const [dayPart, monthPart, yearPart] = dateStr.split("/")
  const day = Number(dayPart)
  const month = Number(monthPart)
  const year = Number(yearPart)

  if (!day || !month || !year) {
    return null
  }

  const date = new Date(year, month - 1, day)

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }

  return startOfDay(date)
}

/** Strips the time component, leaving midnight on the same calendar day. */
function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/** Formats a Date as DD/MM/YYYY for CMS display. */
function formatCmsDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

/** Returns the calendar day before `date` (local midnight). */
function subtractOneDay(date: Date): Date {
  const previousDay = new Date(date.getTime())
  previousDay.setDate(previousDay.getDate() - 1)
  return startOfDay(previousDay)
}

/** Trims and lowercases CSV column keys and values for consistent lookup. */
function normalizeIlbaCsvRow(
  row: IlbaCampaignCsvRow | undefined
): IlbaCampaignCsvRow | null {
  if (!row) {
    return null
  }

  return Object.entries(row).reduce<IlbaCampaignCsvRow>(
    (normalized, [key, value]) => {
      normalized[key.trim().toLowerCase()] =
        typeof value === "string" ? value.trim() : String(value ?? "")
      return normalized
    },
    {}
  )
}

/** Parses one CSV row into concrete campaign dates (campaign_label is ignored). */
function parseIlbaCampaignRow(row: IlbaCampaignCsvRow): IlbaCampaignRow | null {
  const campaignStart = parseCmsDate(row.campaign_start)
  const campaignEnd = parseCmsDate(row.campaign_end)
  const blackoutStart = parseCmsDate(row.blackout_start)
  const blackoutEnd = parseCmsDate(row.blackout_end)

  if (!campaignStart || !campaignEnd || !blackoutStart || !blackoutEnd) {
    return null
  }

  // action_date = last day members can submit; falls back to day before blackout_start when CSV column absent
  const actionDate =
    parseCmsDate(row.action_date) ?? subtractOneDay(blackoutStart)

  return {
    campaignStart,
    campaignEnd,
    actionDate,
    blackoutStart,
    blackoutEnd,
  }
}

/** Builds campaign flags and formatted key dates from a parsed CSV row. */
function buildIlbaCampaignStateFromRow(
  campaign: IlbaCampaignRow,
  now: Date,
  isWithinCampaign: boolean
): IlbaCampaignState {
  const isBeforeBlackout = isWithinCampaign
    ? isBeforeBlackoutPeriod(now, campaign.blackoutStart)
    : false

  return {
    isWithinCampaign,
    isBeforeBlackout,
    // ATO cut-off: hide election UI from blackout_start until blackout_end (show-again date)
    isDuringBlackout:
      isWithinCampaign &&
      !isBeforeBlackout &&
      isDuringBlackoutPeriod(now, campaign.blackoutStart, campaign.blackoutEnd),
    // member-facing election deadline from CSV action_date (distinct from blackout_start hide date)
    actionDate: formatCmsDate(campaign.actionDate),
    blackoutStartDate: formatCmsDate(campaign.blackoutStart),
    blackoutEndDate: formatCmsDate(campaign.blackoutEnd),
  }
}

/**
 * Derives ILBA campaign state from Matrix CSV data.
 * Each row is one campaign with full DD/MM/YYYY dates; the row containing `now` wins.
 */
export function getIlbaCampaignState(
  now: Date,
  csvData: IlbaCampaignCsvRow[] | undefined
): IlbaCampaignState {
  if (!csvData?.length) {
    return EMPTY_ILBA_CAMPAIGN_STATE
  }

  const campaigns = csvData
    .map((row) => {
      const normalized = normalizeIlbaCsvRow(row)
      return normalized ? parseIlbaCampaignRow(normalized) : null
    })
    .filter((campaign): campaign is IlbaCampaignRow => campaign !== null)
    .sort((a, b) => a.campaignStart.getTime() - b.campaignStart.getTime())

  if (campaigns.length === 0) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "[ILBA] CSV rows found but campaign dates could not be parsed."
      )
    }
    return EMPTY_ILBA_CAMPAIGN_STATE
  }

  const today = startOfDay(now)

  for (const campaign of campaigns) {
    if (today >= campaign.campaignStart && today <= campaign.campaignEnd) {
      return buildIlbaCampaignStateFromRow(campaign, today, true)
    }
  }

  // Outside all windows — surface the next upcoming campaign (or the latest) for dev/QA.
  const referenceCampaign =
    campaigns.find((campaign) => campaign.campaignStart > today) ??
    campaigns[campaigns.length - 1]

  return buildIlbaCampaignStateFromRow(referenceCampaign, today, false)
}

/**
 * Checks if the current date is within the campaign period (inclusive).
 */
export function isWithinCampaignPeriod(
  now: Date,
  campaignStart: Date,
  campaignEnd: Date
): boolean {
  return (
    now.getTime() >= campaignStart.getTime() &&
    now.getTime() <= campaignEnd.getTime()
  )
}

/**
 * Checks if the current date is strictly before the blackout start date.
 */
export function isBeforeBlackoutPeriod(
  now: Date,
  blackoutStart: Date
): boolean {
  return now.getTime() < blackoutStart.getTime()
}

/**
 * Hide window [blackout_start, blackout_end): end is exclusive so content
 * returns on the CSV show-again date (ATO cut-off / no Call us path).
 */
export function isDuringBlackoutPeriod(
  now: Date,
  blackoutStart: Date,
  blackoutEnd: Date
): boolean {
  return (
    now.getTime() >= blackoutStart.getTime() &&
    now.getTime() < blackoutEnd.getTime()
  )
}

/**
 * Checks if the expiry date is within 6 months ahead of the current date.
 * Returns false if no date is provided or if the date is invalid.
 */
export function isExpiringWithinSixMonths(
  expiryDate: string | Date | null,
  now: Date
): boolean {
  if (!expiryDate) {
    return false
  }

  const expiry = new Date(expiryDate)

  // Check for an invalid date
  if (isNaN(expiry.getTime())) {
    return false
  }

  const sixMonthsFromNow = new Date(now.getTime())
  sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6)

  return (
    expiry.getTime() > now.getTime() &&
    expiry.getTime() <= sixMonthsFromNow.getTime()
  )
}

/**
 * Checks if the expiry date is within 16 months ahead of the current date.
 * Returns false if no date is provided or if the date is invalid.
 */
export function isExpiringWithinSixteenMonths(
  expiryDate: string | Date | null,
  now: Date
): boolean {
  if (!expiryDate) {
    return false
  }

  const expiry = new Date(expiryDate)

  // Check for an invalid date
  if (isNaN(expiry.getTime())) {
    return false
  }

  const sixteenMonthsFromNow = new Date(now.getTime())
  sixteenMonthsFromNow.setMonth(sixteenMonthsFromNow.getMonth() + 16)

  return (
    expiry.getTime() > now.getTime() &&
    expiry.getTime() <= sixteenMonthsFromNow.getTime()
  )
}
